import C from "@/components/Badge.module.css";
import FF from "@/components/forms/FormField.module.css";

import type { AdminHostWithType } from "@/admin/hosts/hostQuery.ts";
import { hostUpdateMutation } from "@/admin/hosts/hostQuery.ts";
import { ModalHeaderBlock } from "@/admin/hosts/ModalHeaderBlock.tsx";
import { getStatus, type StatusType, statusTypes } from "@/admin/hosts/status.tsx";
import { Badge } from "@/components/Badge.tsx";
import { EnumBadge } from "@/components/EnumBadge.tsx";
import { EditableButton } from "@/components/forms/EditableButton.tsx";
import { WRadio } from "@/components/forms/WRadio.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { cn } from "@/utils/classNames.ts";
import { useState } from "react";
import { RadioGroup } from "react-aria-components";
import { entries } from "remeda";

interface StatusBadgeModalProps {
    item: AdminHostWithType;
    isDot?: boolean;
}

export function StatusBadge({ item, isDot }: StatusBadgeModalProps) {
    const statusKey = getStatus(item);
    const status = statusTypes[statusKey];

    return isDot ? (
        <div className={cn(C.BadgeDot, C.Dot, C[status.color])} />
    ) : (
        <EnumBadge options={statusTypes} value={statusKey} />
    );
}

export function StatusBadgeButton(props: StatusBadgeModalProps) {
    return (
        <WModal
            button={
                <EditableButton action={undefined}>
                    <StatusBadge {...props} />
                </EditableButton>
            }
        >
            <StatusBadgeModalContent {...props} />
        </WModal>
    );
}

function StatusBadgeModalContent({ item }: StatusBadgeModalProps) {
    const defaultStatus = getStatus(item);
    const [status, setStatus] = useState<StatusType>(defaultStatus);
    const updateMutation = useStandardMutation(hostUpdateMutation);

    async function onSubmit() {
        const statuses = statusTypes[status].flags;
        await updateMutation.mutateAsync({
            body: {
                uuid: item.uuid,
                ...statuses,
            },
            location: item.location,
        });
    }

    const radios = entries(statusTypes).map(([key, type]) => {
        const isSelected = status === key;

        return (
            <WRadio
                key={key}
                value={key}
                badge={
                    <Badge reverse={isSelected} noDot color={type.color}>
                        {type.label}
                    </Badge>
                }
                description={type.description}
            />
        );
    });

    return (
        <WModalContent title="Change Hypervisor Status" label="Change" modalAction={async () => await onSubmit()}>
            <ModalHeaderBlock item={item}>
                <StatusBadge item={item} />
            </ModalHeaderBlock>

            <div className="pt-3 pb-2">Change status*</div>
            <RadioGroup
                aria-label="Select status"
                className={cn(FF.FormFieldRadioGroup, "grid-auto-flow-col")}
                value={status}
                onChange={(v) => setStatus(v as StatusType)}
            >
                {radios}

                <p className="color-muted font-size-small grid-col-span-2">
                    Changing the host flag can be a dangerous operation, read the flag description carefully before
                    submitting! Use this feature only if you know what you are doing.
                </p>
            </RadioGroup>
        </WModalContent>
    );
}
